import React from 'react';
import { Alert, Box, Link, Typography } from '@mui/material';
import { Visibility as VisibilityIcon, Handshake as HandshakeIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import CustomerNavbarV2 from './CustomerNavbarV2';
import CustomerFooter from './CustomerFooter';
import CustomerOnboardingDialog from './CustomerOnboardingDialog';
import { useCustomerAuth } from '../../contexts/CustomerAuthContext';
import { useCustomerPortal } from '../../contexts/CustomerPortalContext';

interface CustomerLayoutProps {
  children: React.ReactNode;
}

// Layout del portal cliente. Además del navbar/footer clásicos:
// - banner FIJO «Viendo como cliente (solo lectura)» en modo impersonación;
// - banner «Cuenta gestionada por {partner}» si managed_by_reseller ≠ null;
// - onboarding de bienvenida (primera visita, flag en localStorage).

const CustomerLayout: React.FC<CustomerLayoutProps> = ({ children }) => {
  const { t } = useTranslation();
  const { readOnly, isAuthenticated } = useCustomerAuth();
  const { managedByReseller } = useCustomerPortal();

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        minHeight: '100vh',
        bgcolor: '#ffffff',
      }}
    >
      {/* Banner fijo de impersonación — siempre visible, por encima de todo */}
      {readOnly && (
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: (theme) => theme.zIndex.appBar + 1,
            bgcolor: '#B45309',
            color: '#fff',
            py: 0.75,
            px: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 1
          }}
        >
          <VisibilityIcon fontSize="small" />
          <Typography variant="body2" fontWeight={700}>
            {t('customer.impersonation.banner', 'Viendo como cliente (solo lectura)')}
          </Typography>
        </Box>
      )}

      <CustomerNavbarV2 />

      {/* Cuenta gestionada por un partner: la facturación no se enseña aquí.
          NO se pinta en impersonación: quien mira entonces es el propio
          partner, así que decirle «tu cuenta la gestiona <él mismo>» debajo del
          banner naranja sería una segunda franja que no aporta nada y compite
          con el aviso de solo lectura, que es EL importante. El dato sigue
          visible para él en el bloque de «Mi cuenta», que es la vista del
          cliente que ha venido a ver. */}
      {isAuthenticated && managedByReseller && !readOnly && (
        <Alert
          severity="info"
          icon={<HandshakeIcon fontSize="inherit" />}
          sx={{ borderRadius: 0, justifyContent: 'center' }}
        >
          {t('customer.managed.banner', 'Tu cuenta la gestiona {{name}}', { name: managedByReseller.name })}
          {managedByReseller.contact_email ? (
            <>
              {' · '}
              <Link href={`mailto:${managedByReseller.contact_email}`} color="inherit" underline="always">
                {managedByReseller.contact_email}
              </Link>
            </>
          ) : ''}
        </Alert>
      )}

      <Box
        component="main"
        sx={{
          flexGrow: 1,
        }}
      >
        {children}
      </Box>
      <CustomerFooter />
      <CustomerOnboardingDialog />
    </Box>
  );
};

export default CustomerLayout;
